import React, { useState, useEffect } from 'react';
import { ClockIcon as Clock, PlayIcon as Play, PauseIcon as Pause, StopIcon as Square, XCircleIcon as XCircle, ClockIcon as Timer, CogIcon as Settings } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';

interface TimeSession {
  user_id: string;
  seat_id: string;
  ticket_id: string;
  ticket_type: string;
  start_time: string;
  initial_remaining_time: number;
  status: 'active' | 'paused' | 'completed';
}

interface UsageXCircle {
  is_active: boolean;
  session?: TimeSession;
  elapsed_time: number;
  elapsed_time_formatted: string;
  remaining_time: number;
  remaining_time_formatted: string;
  estimated_end_time?: string;
  usage_percentage: number;
}

interface AvailableTicket {
  id: string;
  type: string;
  name: string;
  remaining_time: number;
  expires_at?: string;
}

const TimeTracker: React.FC = () => {
  const [usageXCircle, setUsageXCircle] = useState<UsageXCircle | null>(null);
  const [availableTickets, setAvailableTickets] = useState<AvailableTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeat, setSelectedSeat] = useState<string>('');
  const [selectedTicketType, setSelectedTicketType] = useState<string>('auto');

  // 실시간 업데이트를 위한 interval
  useEffect(() => {
    const fetchUsageXCircle = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/v1/time-tracking/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('사용 정보를 가져올 수 없습니다');
        }

        const data = await response.json();
        if (data.success) {
          setUsageXCircle(data.usage_info);
        }
      } catch (err) {
        console.error('Usage info fetch error:', err);
        setError(err instanceof Error ? err.message : '알 수 없는 오류');
      } finally {
        setIsLoading(false);
      }
    };

    // 초기 로드
    fetchUsageXCircle();

    // 활성 세션이 있을 때만 5초마다 업데이트
    const interval = setInterval(() => {
      if (usageXCircle?.is_active) {
        fetchUsageXCircle();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [usageXCircle?.is_active]);

  // 이용권 목록 조회
  useEffect(() => {
    const fetchAvailableTickets = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/v1/time-tracking/available-tickets`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setAvailableTickets(data.tickets);
          }
        }
      } catch (err) {
        console.error('Available tickets fetch error:', err);
      }
    };

    fetchAvailableTickets();
  }, []);

  const startTracking = async () => {
    if (!selectedSeat) {
      alert('좌석을 선택해주세요');
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/time-tracking/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          seat_id: selectedSeat,
          ticket_type: selectedTicketType
        })
      });

      const data = await response.json();
      if (data.success) {
        // 성공 시 사용 정보 새로고침
        window.location.reload();
      } else {
        setError(data.detail || '시간 추적 시작에 실패했습니다');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '시간 추적 시작에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  const stopTracking = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/time-tracking/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: '사용자 종료'
        })
      });

      const data = await response.json();
      if (data.success) {
        setUsageXCircle(null);
        alert(`사용이 완료되었습니다.\n사용 시간: ${data.session_summary?.used_time_formatted}`);
      } else {
        setError(data.detail || '시간 추적 중지에 실패했습니다');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '시간 추적 중지에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  const pauseTracking = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/time-tracking/pause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        // 사용 정보 새로고침
        window.location.reload();
      } else {
        setError(data.detail || '일시정지에 실패했습니다');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '일시정지에 실패했습니다');
    }
  };

  const resumeTracking = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/time-tracking/resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        // 사용 정보 새로고침
        window.location.reload();
      } else {
        setError(data.detail || '재개에 실패했습니다');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '재개에 실패했습니다');
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getWarningLevel = (remainingMinutes: number) => {
    if (remainingMinutes <= 5) return 'critical';
    if (remainingMinutes <= 30) return 'warning';
    return 'normal';
  };

  if (isLoading && !usageXCircle) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">로딩 중...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center mb-6">
        <Timer className="h-6 w-6 text-blue-600 mr-2" />
        <h2 className="text-xl font-semibold text-gray-900">시간 추적</h2>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <XCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {usageXCircle?.is_active ? (
        <div className="space-y-6">
          {/* 현재 세션 정보 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-2 ${
                  usageXCircle.session?.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'
                }`}></div>
                <span className="font-medium">
                  {usageXCircle.session?.status === 'active' ? '사용 중' : '일시정지'}
                </span>
              </div>
              <span className="text-sm text-gray-500">
                좌석 {usageXCircle.session?.seat_id}
              </span>
            </div>

            {/* 시간 정보 */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {usageXCircle.elapsed_time_formatted}
                </div>
                <div className="text-sm text-gray-500">사용 시간</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${
                  getWarningLevel(usageXCircle.remaining_time) === 'critical' ? 'text-red-600' :
                  getWarningLevel(usageXCircle.remaining_time) === 'warning' ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {usageXCircle.remaining_time_formatted}
                </div>
                <div className="text-sm text-gray-500">남은 시간</div>
              </div>
            </div>

            {/* 진행률 바 */}
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>진행률</span>
                <span>{usageXCircle.usage_percentage.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(usageXCircle.usage_percentage)}`}
                  style={{ width: `${Math.min(usageXCircle.usage_percentage, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* 이용권 정보 */}
            <div className="text-sm text-gray-600 mb-4">
              <span className="font-medium">이용권:</span> {usageXCircle.session?.ticket_type === 'time' ? '시간권' : 
                usageXCircle.session?.ticket_type === 'monthly' ? '정기권' : '무제한권'}
            </div>

            {/* 예상 종료 시간 */}
            {usageXCircle.estimated_end_time && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">예상 종료:</span> {
                  new Date(usageXCircle.estimated_end_time).toLocaleTimeString('ko-KR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                }
              </div>
            )}
          </div>

          {/* 경고 메시지 */}
          {getWarningLevel(usageXCircle.remaining_time) !== 'normal' && (
            <div className={`p-4 rounded-md ${
              getWarningLevel(usageXCircle.remaining_time) === 'critical' 
                ? 'bg-red-50 border border-red-200' 
                : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <div className="flex items-center">
                <XCircle className={`h-5 w-5 mr-2 ${
                  getWarningLevel(usageXCircle.remaining_time) === 'critical' ? 'text-red-500' : 'text-yellow-500'
                }`} />
                <p className={`text-sm ${
                  getWarningLevel(usageXCircle.remaining_time) === 'critical' ? 'text-red-700' : 'text-yellow-700'
                }`}>
                  {getWarningLevel(usageXCircle.remaining_time) === 'critical'
                    ? '시간이 거의 다 되었습니다! 연장이 필요한지 확인해주세요.'
                    : '시간이 얼마 남지 않았습니다. 준비해주세요.'}
                </p>
              </div>
            </div>
          )}

          {/* 제어 버튼 */}
          <div className="flex gap-3">
            {usageXCircle.session?.status === 'active' ? (
              <button
                onClick={pauseTracking}
                className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
              >
                <Pause className="h-4 w-4 mr-2" />
                일시정지
              </button>
            ) : (
              <button
                onClick={resumeTracking}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Play className="h-4 w-4 mr-2" />
                재개
              </button>
            )}
            <button
              onClick={stopTracking}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              <Square className="h-4 w-4 mr-2" />
              종료
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* 시간 추적 시작 */}
          <div className="text-center py-8">
            <Clock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">시간 추적을 시작하세요</h3>
            <p className="text-gray-600">좌석을 선택하고 시간 추적을 시작할 수 있습니다.</p>
          </div>

          {/* 좌석 선택 */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                좌석 선택
              </label>
              <select
                value={selectedSeat}
                onChange={(e) => setSelectedSeat(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">좌석을 선택하세요</option>
                {Array.from({ length: 50 }, (_, i) => i + 1).map(num => (
                  <option key={num} value={`SEAT-${num.toString().padStart(2, '0')}`}>
                    좌석 {num}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                이용권 선택
              </label>
              <select
                value={selectedTicketType}
                onChange={(e) => setSelectedTicketType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="auto">자동 선택 (추천)</option>
                <option value="unlimited">무제한권 우선</option>
                <option value="monthly">정기권 우선</option>
                <option value="time">시간권 우선</option>
              </select>
            </div>
          </div>

          {/* 이용 가능한 이용권 */}
          {availableTickets.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">이용 가능한 이용권</h4>
              <div className="space-y-2">
                {availableTickets.map((ticket) => (
                  <div key={ticket.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div>
                      <span className="font-medium">{ticket.name}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        ({ticket.type === 'time' ? `${Math.floor(ticket.remaining_time / 60)}시간 ${ticket.remaining_time % 60}분` : '무제한'})
                      </span>
                    </div>
                    {ticket.expires_at && (
                      <span className="text-xs text-gray-500">
                        만료: {new Date(ticket.expires_at).toLocaleDateString('ko-KR')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 시작 버튼 */}
          <button
            onClick={startTracking}
            disabled={!selectedSeat || isLoading}
            className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-5 w-5 mr-2" />
            시간 추적 시작
          </button>
        </div>
      )}
    </div>
  );
};

export default TimeTracker;